import React, { Fragment, useContext, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { reduxForm, Field, change } from 'redux-form';
import { ButtonToolbar, Button } from 'reactstrap';
import PropTypes from 'prop-types';
import renderField from '../../../shared/components/form/RenderField';
import Select from '../../../shared/components/form/Select';
import DateField from '../../../shared/components/form/DateField';
import MaterialTable from '../../../shared/components/material-table';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import columns from './ColumnsPresence';
import {
  ATTACHMENT_TYPES,
  PRESENCE_TYPE,
  SUPPORTED_POST_FORMATS,
} from '../../../shared/utils/constants';
import validate from './Validate';
import SelectVirtualized from '../../../shared/components/form/SelectVirtualized';
import SmartContext from '../../App/context/SmartContext';
import renderDropZoneMultipleField from '../../../shared/components/form/DropZoneMultiple';
import { professorScheduleAvailableClasses } from '../../../shared/queries/TeacherRoom';
import findCycleByCalendarAndDate from '../../../shared/queries/AcademicCalendarCycle';
import GraphQL from '../../../GraphQL';

const ClassDiaryForm = (props) => {
  const {
    t,
    handleSubmit,
    dto,
    initialize,
    backClickHandler,
    studentAttendance,
    onChangeStudentAttendance,
    dispatch,
    schoolClasses,
    onChangeClassSequenceInStudentAttendance,
    loadSubjectBySchoolClass,
    subjects,
    listStudentAttendance,
    disabledDates,
    listDisabledDates,
    showLoading,
    hideLoading,
  } = props;

  const [selectedDate, setSelectedDate] = useState();
  const [classes, setClasses] = useState([]);
  const [selectedSchoolClassData, setSelectedSchoolClassData] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState();
  const [supportedMimeTypes, setSupportedMimeTypes] = useState();
  const [supportedExtensions, setSupportedExtensions] = useState();
  const context = useContext(SmartContext);
  const { removeFiles, addFile } = context;

  const loadAvailableClasses = (date, schoolClassId, subjectId) => {
    showLoading();
    const query = professorScheduleAvailableClasses(
      schoolClassId,
      subjectId,
      date
    );
    GraphQL(query, {})
      .then((res) => {
        if (
          res.professorScheduleAvailableClasses &&
          res.professorScheduleAvailableClasses.length
        ) {
          setClasses(res.professorScheduleAvailableClasses);
        } else {
          showNotificationAutoDestroy(
            'warning',
            t('warnings.title'),
            t('notifications.class-diary-no-class-available')
          );
        }
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const loadAcademicCalendarCycle = (academicCalendar, date) => {
    if (academicCalendar) {
      const query = findCycleByCalendarAndDate(academicCalendar.id, date);
      GraphQL(query, {})
        .then((res) => {
          if (res.findCycleByCalendarAndDate) {
            dispatch(
              change(
                'ClassDiaryForm',
                'cycle',
                res.findCycleByCalendarAndDate.sequence
              )
            );
          } else {
            showNotificationAutoDestroy(
              'danger',
              t('errors.title'),
              t('errors.error-to-loading-academiccyle')
            );
          }
        })
        .catch((error) => {
          console.error('file: Form.jsx ~ line 93 ~ GraphQL ~ error', error);
          showNotificationAutoDestroy(
            'danger',
            t('errors.title'),
            t('errors.error-to-loading-academiccyle')
          );
        });
    }
  };

  const clearFieldClassSequence = () => {
    setClasses([]);
    dispatch(change('ClassDiaryForm', 'classSequence', null));
  };

  const clearFieldSubject = () => {
    dispatch(change('ClassDiaryForm', 'subject', null));
  };

  const clearFieldDateInMilli = () => {
    dispatch(change('ClassDiaryForm', 'dateInMilli', null));
  };

  const clearClassesSequenceInStudentAttendance = () => {
    onChangeClassSequenceInStudentAttendance();
  };

  const onChangeSelectedSubject = (value, changeFormState = true) => {
    if (value) {
      listDisabledDates(selectedSchoolClassData.schoolClass.id, value.id);
      setSelectedSubject({ ...value });
    } else {
      setSelectedSubject({});
      setSelectedDate(null);
    }
    if (changeFormState) {
      clearFieldClassSequence();
      clearFieldDateInMilli();
    }
    clearClassesSequenceInStudentAttendance();
  };

  const onChangeSelectedDate = (value) => {
    setSelectedDate(value);
    loadAvailableClasses(
      value,
      selectedSchoolClassData.schoolClass.id,
      selectedSubject.id
    );
    loadAcademicCalendarCycle(selectedSchoolClassData.academicCalendar, value);
    clearFieldClassSequence();
    clearClassesSequenceInStudentAttendance();
  };

  const changePresence = (rowData, typePresence, attributeName) => {
    let newTypePresence;
    switch (typePresence) {
      case PRESENCE_TYPE.PRESENT:
        newTypePresence = PRESENCE_TYPE.LACK;
        break;
      case PRESENCE_TYPE.LACK:
        newTypePresence = PRESENCE_TYPE.JUSTIFIED;
        break;
      case PRESENCE_TYPE.JUSTIFIED:
        newTypePresence = PRESENCE_TYPE.PRESENT;
        break;
      default:
    }
    const index = studentAttendance.findIndex((element) => element === rowData);
    const updatedData = [...studentAttendance];
    updatedData[index][attributeName] = {
      ...rowData[attributeName],
      attendanceType: newTypePresence,
    };
    onChangeStudentAttendance(updatedData);
  };

  const setStateFormByCourseStepAndShift = (value) => {
    let courseStepValue = null;
    let classShiftValue = null;
    if (value) {
      if (value.classShift) {
        classShiftValue = value.classShift;
      }
      if (value.courseStep) {
        courseStepValue = value.courseStep;
      }
    }
    dispatch(change('ClassDiaryForm', 'courseStep', courseStepValue));
    dispatch(change('ClassDiaryForm', 'shift', classShiftValue));
  };

  const getClassesSequences = (values) => values.map(({ value }) => value);

  const changeValuesForm = (value) => {
    setStateFormByCourseStepAndShift(value);
    setSelectedSchoolClassData(value);
    clearFieldSubject();
    clearFieldClassSequence();
    loadSubjectBySchoolClass(value.schoolClass);
    clearFieldDateInMilli();
    setSelectedSubject({});
  };

  const findForSchoolClass = async (schoolClassId) => {
    const schoolClass = schoolClasses.find(
      (item) => item.schoolClass.id === schoolClassId
    );
    await setStateFormByCourseStepAndShift(schoolClass);
    setSelectedSchoolClassData(schoolClass);
  };

  const onDropZoneChange = (values) => {
    values.forEach((element) => {
      if (element.uploadType === ATTACHMENT_TYPES.VIMEO) {
        addFile({
          status: 'pending',
          content: element,
        });
      }
    });
    if (values) {
      change('files', values);
    } else {
      change('files', []);
    }
    removeFiles(values);
  };

  const loadSupportedFormats = () => {
    const mappedMimeTypes = SUPPORTED_POST_FORMATS.map(
      (formatType) => formatType.mimeType
    );
    const mappedDescriptions = SUPPORTED_POST_FORMATS.map(
      (formatType) => formatType.description
    );
    setSupportedMimeTypes(mappedMimeTypes);
    setSupportedExtensions(mappedDescriptions);
  };

  const prepareValues = (values) => {
    let classesSequences = [];
    if (values && values.length) {
      classesSequences = getClassesSequences(values);
    }
    const { schoolClass, subject } = selectedSchoolClassData;
    listStudentAttendance(
      selectedDate.getTime(),
      schoolClass,
      subject.id,
      classesSequences
    );
  };

  useEffect(() => {
    dispatch(change('ClassDiaryForm', 'studentAttendance', studentAttendance));
  }, [studentAttendance]);

  useEffect(() => {
    initialize(dto);
    if (dto.id === 0 && dto.schoolClass) {
      findForSchoolClass(dto.schoolClass.id).then(() => {
        listStudentAttendance(
          dto.dateInMilli.getTime(),
          dto.schoolClass,
          dto.subject.id,
          getClassesSequences(dto.classSequence)
        );
      });
      setSelectedSubject(dto.subject);
      loadSubjectBySchoolClass(dto.schoolClass);
      setSelectedSchoolClassData(dto.schoolClass);
      listDisabledDates(dto.schoolClass.id, dto.subject.id);
      setSelectedDate(dto.dateInMilli);
      loadAvailableClasses(
        new Date(dto.dateInMilli),
        dto.schoolClass.id,
        dto.subject.id
      );
      if (
        dto.schoolClass &&
        dto.schoolClass.academicCalendar &&
        dto.dateInMilli
      ) {
        loadAcademicCalendarCycle(
          dto.schoolClass.academicCalendar,
          new Date(dto.dateInMilli)
        );
      }
    }
  }, [dto, schoolClasses]);

  useEffect(() => {
    loadSupportedFormats();
  }, []);

  return (
    <Fragment>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form__form-group--3_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.school-class')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="schoolClass"
                component={SelectVirtualized}
                options={schoolClasses.map((item) => ({
                  ...item,
                  label: item.schoolClass.name,
                  value: item.schoolClass.id,
                  id: item.schoolClass.id,
                }))}
                onChange={(value) => changeValuesForm(value)}
                placeholder={t('general.placeholders.select')}
                isDisabled={dto.id > 0}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">{t('general.shift')}</span>
            <div className="form_form-group-field">
              <Field
                name="shift"
                component={Select}
                options={[]}
                isDisabled
                placeholder={t('general.placeholders.select')}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.year-level')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="courseStep"
                component={Select}
                options={[]}
                isDisabled
                placeholder={t('general.placeholders.select')}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group--3_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.discipline')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="subject"
                component={Select}
                options={subjects}
                onChange={(value) => onChangeSelectedSubject(value)}
                placeholder={t('general.placeholders.select')}
                isDisabled={dto.id > 0}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">{t('general.date')}</span>
            <div className="form_form-group-field">
              <Field
                name="dateInMilli"
                component={DateField}
                onChange={onChangeSelectedDate}
                excludeDates={disabledDates}
                disabled={!selectedSubject || !selectedSubject.id || dto.id > 0}
                placeholder={t('general.placeholders.select')}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.single-class')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="classSequence"
                component={Select}
                closeMenuOnSelect={classes.length === 1}
                isDisabled={dto.id > 0}
                options={classes.map((sequence) => ({
                  ...sequence,
                  value: sequence,
                  label: `${sequence}º ${t('general.single-class')}`,
                }))}
                isMulti
                onChange={prepareValues}
                placeholder={t('general.placeholders.select')}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.subject')}</span>
          <div className="form__form-group-field">
            <Field
              name="workSubject"
              type="text"
              component={renderField}
              placeholder={t('general.placeholders.type-here')}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('general.description')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="description"
              type="textarea"
              component={renderField}
              placeholder={t('general.placeholders.type-here')}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.files')}</span>
          <div className="form__form-group-field">
            <Field
              name="files"
              component={renderDropZoneMultipleField}
              onChange={onDropZoneChange}
              accept={supportedMimeTypes}
              fileExtensions={supportedExtensions}
              showYouTubeField={false}
            />
          </div>
        </div>
        <div className="form__form-group">
          <MaterialTable
            columns={columns(t, changePresence, studentAttendance)}
            data={studentAttendance}
            maxBodyHeightAuto
            paging={false}
            toolbar={false}
            showTitle={false}
            search={false}
          />
        </div>
        <ButtonToolbar className="form__button-toolbar">
          <Button color="primary" onClick={backClickHandler}>
            {t('general.back')}
          </Button>
          <Button color="success" type="submit">
            {t('general.save')}
          </Button>
        </ButtonToolbar>
      </form>
    </Fragment>
  );
};

ClassDiaryForm.propTypes = {
  t: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  backClickHandler: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  initialize: PropTypes.func.isRequired,
  schoolClasses: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
  dispatch: PropTypes.func.isRequired,
  onChangeStudentAttendance: PropTypes.func.isRequired,
  onChangeClassSequenceInStudentAttendance: PropTypes.func.isRequired,
  studentAttendance: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape))
    .isRequired,
  subjects: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
  listStudentAttendance: PropTypes.func.isRequired,
  loadSubjectBySchoolClass: PropTypes.func.isRequired,
  listDisabledDates: PropTypes.func.isRequired,
  disabledDates: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
};

ClassDiaryForm.defaultProps = {
  disabledDates: [],
};

export default reduxForm({
  form: 'ClassDiaryForm',
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
  validate,
})(withTranslation('common')(ClassDiaryForm));
